<script setup lang="ts">
import { computed } from 'vue'
import { mdiLoading } from '@mdi/js'
import type { Validate } from '@/shared/types/validation.types'
import SvgIcon from '@/shared/components/icon/SvgIcon.vue'

interface Props {
  title?: string
  text?: string
  icon?: string
  loading?: boolean
  disabled?: boolean
}

interface Emits {
  (e: 'click'): void
}

defineProps<Props>()
const emits = defineEmits<Emits>()
const model = defineModel<{ [key: string]: Validate }>()

const getValidation = computed((): Validate | undefined => {
  return model.value
    ? Object.values(model.value).find((validation) => validation?.isInvalid)
    : undefined
})

const onValidation = (): void => {
  if (model.value) {
    const validation = getValidation.value
    if (validation) {
      validation.isDirty = true
    } else {
      emits('click')
    }
  } else {
    emits('click')
  }
}
</script>

<template>
  <button
    type="button"
    class="group inline-flex items-center border transition-colors duration-300 border-brand text-brand-contrast bg-brand p-2 enabled:cursor-pointer enabled:hover:shadow-inner disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-400 dark:disabled:bg-gray-800 disabled:border-dashed dark:disabled:text-black"
    :title="title || text"
    :class="[
      getValidation?.isDirty && !disabled
        ? 'enabled:text-white enabled:bg-error enabled:border-error enabled:hover:bg-error/80 enabled:hover:border-error active:animate-wiggle'
        : 'text-brand-contrast enabled:hover:text-surface enabled:hover:bg-surface-inverse enabled:hover:border-surface-inverse enabled:hover:dark:bg-brand',
    ]"
    @click="onValidation()"
    :disabled="loading || disabled"
    v-bind="$attrs"
  >
    <SvgIcon v-if="loading" :path="mdiLoading" class="animate-spin h-5 w-5" />
    <SvgIcon
      v-else
      :path="icon"
      class="h-5 w-5 group-disabled:fill-gray-400"
      :class="
        getValidation?.isDirty ? 'fill-white' : 'fill-brand-contrast group-hover:fill-surface'
      "
    />
    <span v-if="text" class="ms-1.5 me-2 text-sm text-nowrap">{{ text }}</span>
  </button>
</template>
